import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Eye, EyeOff, ShoppingCart, Globe, Upload } from "lucide-react";

const safeFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    const headers = options.headers || {};
    Object.entries(headers).forEach(([key, value]) => {
      if (value) xhr.setRequestHeader(key, value);
    });
    xhr.onload = function() {
      let data;
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : null; }
      catch { data = xhr.responseText; }
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, json: () => Promise.resolve(data) });
    };
    xhr.onerror = () => reject(new Error('Network error'));
    if (options.body) xhr.send(typeof options.body === 'string' ? options.body : JSON.stringify(options.body));
    else xhr.send();
  });
};

const emptyService = {
  name: "", slug: "", description: "", icon_url: "", fare_amount: "",
  multiplier: "1.0", commission_override: "", default_eta: "",
  is_active: true, show_on_homepage: true, show_in_booking: true
};

export default function ServiceManagement({ token }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({ open: false, mode: "add", service: null });
  const [form, setForm] = useState({ ...emptyService });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, slug: "" });

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const fetchOpts = { headers };

  const fetchServices = useCallback(async () => {
    try {
      const res = await safeFetch(`${API}/admin/service-types`, fetchOpts);
      if (res.ok) setServices(await res.json());
    } catch (e) { console.error("Operation failed:", e); }
  }, [token]);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  const openAdd = () => {
    setForm({ ...emptyService });
    setDialog({ open: true, mode: "add", service: null });
  };

  const openEdit = (svc) => {
    setForm({
      name: svc.name || "", slug: svc.slug || "", description: svc.description || "",
      icon_url: svc.icon_url || "", fare_amount: svc.fare_amount || "",
      multiplier: svc.multiplier || "1.0", commission_override: svc.commission_override ?? "",
      default_eta: svc.default_eta || "", is_active: svc.is_active !== false,
      show_on_homepage: svc.show_on_homepage !== false, show_in_booking: svc.show_in_booking !== false,
    });
    setDialog({ open: true, mode: "edit", service: svc });
  };

  const handleSave = async () => {
    if (!form.name || !form.slug || !form.fare_amount) {
      toast.error("Name, slug, and base fare are required");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        fare_amount: parseFloat(form.fare_amount),
        multiplier: parseFloat(form.multiplier) || 1.0,
        commission_override: form.commission_override ? parseFloat(form.commission_override) : null,
      };

      if (dialog.mode === "add") {
        const res = await safeFetch(`${API}/admin/service-types`, {
          method: "POST", ...fetchOpts, body: JSON.stringify(payload),
        });
        if (res.ok) { toast.success("Service created"); setDialog({ open: false, mode: "add", service: null }); fetchServices(); }
        else { const d = await res.json(); toast.error(d.detail || "Failed"); }
      } else {
        const res = await safeFetch(`${API}/admin/service-types/${dialog.service.slug}`, {
          method: "PUT", ...fetchOpts, body: JSON.stringify(payload),
        });
        if (res.ok) { toast.success("Service updated"); setDialog({ open: false, mode: "add", service: null }); fetchServices(); }
        else toast.error("Failed to update");
      }
    } catch (e) { console.error(e); toast.error("Network error"); }
    setLoading(false);
  };

  const handleDelete = async (slug) => {
    try {
      const res = await safeFetch(`${API}/admin/service-types/${slug}`, { method: "DELETE", ...fetchOpts });
      if (res.ok) { toast.success("Service deleted"); fetchServices(); }
      else toast.error("Failed to delete");
    } catch (e) { console.error(e); toast.error("Network error"); }
    setDeleteConfirm({ open: false, slug: "" });
  };

  const handleToggle = async (svc, field) => {
    try {
      await safeFetch(`${API}/admin/service-types/${svc.slug}`, {
        method: "PUT", ...fetchOpts,
        body: JSON.stringify({ [field]: !svc[field] }),
      });
      fetchServices();
    } catch (e) { console.error("Operation failed:", e); }
  };

  const handleIconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API}/upload`, true);
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          const data = JSON.parse(xhr.responseText);
          setForm(f => ({ ...f, icon_url: data.url || data.file_url || "" }));
          toast.success("Icon uploaded");
        }
      };
      xhr.send(formData);
    } catch (e) { console.error("Operation failed:", e); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="font-heading text-xl font-semibold text-slate-900" data-testid="service-mgmt-title">Service Management</h2>
          <p className="text-sm text-slate-500 mt-1">{services.length} services configured</p>
        </div>
        <Button data-testid="add-service-btn" onClick={openAdd} className="bg-amber-500 hover:bg-amber-600 text-white rounded-full">
          <Plus className="w-4 h-4 mr-2" /> Add Service
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {services.map((svc) => (
          <Card key={svc.slug} data-testid={`service-card-${svc.slug}`} className={`rounded-2xl border-0 shadow-sm transition-all ${!svc.is_active ? "opacity-50" : ""}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  {svc.icon_url ? (
                    <img src={svc.icon_url.startsWith("http") ? svc.icon_url : `${API.replace('/api', '')}/${svc.icon_url}`} alt="" className="w-10 h-10 rounded-lg object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-forest/10 flex items-center justify-center">
                      <ShoppingCart className="w-5 h-5 text-forest" />
                    </div>
                  )}
                  <div>
                    <h4 className="font-heading font-semibold text-slate-900">{svc.name}</h4>
                    <p className="text-xs text-slate-400">{svc.slug}</p>
                  </div>
                </div>
                <Badge className={`rounded-full text-xs ${svc.is_active ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                  {svc.is_active ? "Active" : "Inactive"}
                </Badge>
              </div>
              {svc.description && <p className="text-xs text-slate-500 mb-3 line-clamp-2">{svc.description}</p>}
              <div className="flex items-end justify-between">
                <div>
                  <p className="font-heading font-bold text-2xl text-slate-900">{"\u20B9"}{svc.fare_amount}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                    <span>x{svc.multiplier}</span>
                    {svc.default_eta && <span>ETA: {svc.default_eta}</span>}
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <div className="flex items-center gap-1">
                    {svc.show_on_homepage && <Globe className="w-3.5 h-3.5 text-blue-500" title="On Homepage" />}
                    {svc.show_in_booking && <ShoppingCart className="w-3.5 h-3.5 text-green-500" title="In Booking" />}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100">
                <Button data-testid={`edit-service-${svc.slug}`} variant="outline" size="sm" onClick={() => openEdit(svc)} className="rounded-full text-xs flex-1">
                  <Edit className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button data-testid={`delete-service-${svc.slug}`} variant="outline" size="sm" onClick={() => setDeleteConfirm({ open: true, slug: svc.slug })} className="rounded-full text-xs text-red-500 border-red-200 hover:bg-red-50">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialog.open} onOpenChange={(o) => setDialog(d => ({ ...d, open: o }))}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="service-dialog">
          <DialogHeader>
            <DialogTitle>{dialog.mode === "add" ? "Add New Service" : "Edit Service"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input data-testid="service-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: dialog.mode === "add" ? e.target.value.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "") : form.slug })} />
              </div>
              <div>
                <Label>Slug *</Label>
                <Input data-testid="service-slug-input" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} disabled={dialog.mode === "edit"} />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea data-testid="service-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
            </div>
            <div>
              <Label>Icon</Label>
              <div className="flex items-center gap-3">
                {form.icon_url && <img src={form.icon_url.startsWith("http") ? form.icon_url : `${API.replace('/api', '')}/${form.icon_url}`} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 text-xs text-slate-500">
                  <Upload className="w-4 h-4" /> Upload Icon
                  <input type="file" accept="image/*" onChange={handleIconUpload} className="hidden" />
                </label>
                <Input placeholder="Or paste URL" value={form.icon_url} onChange={(e) => setForm({ ...form, icon_url: e.target.value })} className="flex-1 text-xs" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Base Fare ({"\u20B9"}) *</Label>
                <Input data-testid="service-fare-input" type="number" value={form.fare_amount} onChange={(e) => setForm({ ...form, fare_amount: e.target.value })} />
              </div>
              <div>
                <Label>Multiplier</Label>
                <Input type="number" step="0.1" value={form.multiplier} onChange={(e) => setForm({ ...form, multiplier: e.target.value })} />
              </div>
              <div>
                <Label>Commission Override</Label>
                <Input type="number" placeholder="Optional" value={form.commission_override} onChange={(e) => setForm({ ...form, commission_override: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Default ETA</Label>
              <Input placeholder="e.g. 30 min, 2-4 hrs" value={form.default_eta} onChange={(e) => setForm({ ...form, default_eta: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-4 pt-2">
              <div className="flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <Label className="text-xs">Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.show_on_homepage} onCheckedChange={(v) => setForm({ ...form, show_on_homepage: v })} />
                <Label className="text-xs">Homepage</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.show_in_booking} onCheckedChange={(v) => setForm({ ...form, show_in_booking: v })} />
                <Label className="text-xs">Booking</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(d => ({ ...d, open: false }))} className="rounded-full">Cancel</Button>
            <Button data-testid="save-service-btn" onClick={handleSave} disabled={loading} className="bg-forest hover:bg-forest/90 text-white rounded-full">
              {loading ? "Saving..." : "Save Service"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirm.open} onOpenChange={(o) => setDeleteConfirm(d => ({ ...d, open: o }))}>
        <DialogContent className="max-w-sm" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Delete Service?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">This will permanently remove this service type. Existing bookings will not be affected.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm({ open: false, slug: "" })} className="rounded-full">Cancel</Button>
            <Button data-testid="confirm-delete-btn" onClick={() => handleDelete(deleteConfirm.slug)} className="bg-red-500 hover:bg-red-600 text-white rounded-full">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
